import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Spacing from '../Spacing';
import { pageTitle } from '../../helpers/PageTitle';
import { authAPI } from '../../services/api';

const validationSchema = Yup.object({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required')
});

export default function SignInPage() {
  pageTitle('Sign In - Ayumedix');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setLoading(true);
    try {
      const credentials = {
        emailID: values.email,
        password: values.password
      };
      
      // console.log('Sending login request:', credentials);
      const response = await authAPI.login(credentials);
      // console.log('Login response:', response);
      
      if (response.data.statusCode === 200 && response.data.data && response.data.data.length > 0) {
        const userData = response.data.data[0];
        
        // Store user data and token separately
        const userInfo = {
          userID: userData.userID,
          name: userData.fullName,
          email: userData.emailID,
          mobile: userData.mobileNo,
          address: userData.address || '',
          isAuthenticated: true
        };
        localStorage.setItem('user', JSON.stringify(userInfo));
        localStorage.setItem('token', userData.token);
        
        toast.success('Login successful! Welcome back.', {
          position: 'top-right',
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        setTimeout(() => navigate('/'), 1000);
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      // console.error('Login error:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <Spacing lg="70" md="70" />
      <section className="cs_primary_bg">
        <Spacing lg="100" md="80" />
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-5 col-md-7">
              <div className="cs_signup_form_wrap" style={{
                background: 'rgba(0, 0, 0, 0.8)',
                borderRadius: '15px',
                padding: '40px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div className="cs_signup_form">
                  <div className="text-center mb-4">
                    <img src="/images/ayumedix.png" width={150} alt="Ayumedix Logo" />
                    <h2 className="cs_fs_40 cs_semibold mt-3 cs_white_color">
                      Welcome Back
                    </h2>
                    <p className="cs_fs_16 cs_white_color">
                      Sign in to your Ayumedix account
                    </p>
                  </div>
                  
                  <Formik
                    initialValues={{
                      email: '',
                      password: ''
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                  >
                    {({ isSubmitting }) => (
                      <Form>
                        <div className="row">
                          <div className="col-12">
                            <label className="cs_fs_18 cs_semibold cs_white_color">
                              Email Address *
                            </label>
                            <Field
                              type="email"
                              name="email"
                              placeholder="Enter your email address"
                              className="cs_form_field"
                            />
                            <ErrorMessage name="email" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                            <Spacing lg="25" md="20" />
                          </div>
                          
                          <div className="col-12">
                            <label className="cs_fs_18 cs_semibold cs_white_color">
                              Password *
                            </label>
                            <Field
                              type="password"
                              name="password"
                              placeholder="Enter your password"
                              className="cs_form_field"
                            />
                            <ErrorMessage name="password" component="div" style={{color: '#ff6b6b', fontSize: '14px', marginTop: '5px'}} />
                            <div className="text-right mt-2">
                              <Link to="/forgot-password" className="cs_fs_14 cs_accent_color">
                                Forgot Password?
                              </Link>
                            </div>
                            <Spacing lg="30" md="25" />
                          </div>
                          
                          <div className="col-12 text-center">
                            <button 
                              type="submit" 
                              className="cs_btn cs_style_1 cs_fs_16"
                              disabled={isSubmitting || loading}
                            >
                              {loading ? 'Signing In...' : 'Sign In'}
                              {!loading && (
                                <span>
                                  <i><Icon icon="fa6-solid:arrow-right" /></i>
                                  <i><Icon icon="fa6-solid:arrow-right" /></i>
                                </span>
                              )}
                            </button>
                            <Spacing lg="20" md="15" />
                            <p className="cs_fs_14 cs_white_color">
                              Don't have an account? 
                              <Link to="/signup" className="cs_accent_color cs_semibold"> Sign Up</Link>
                            </p>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Spacing lg="100" md="80" />
      </section>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </>
  );
}